import { SUBSCRIPTION_STATUS } from '@adtrackify/at-tracking-event-types';

import { BANNER_TONE } from '@/components/ui/types/banner-variants-enums';
import { getEffectiveTrialState } from '@/features/billing/helpers/trial-indicator';
import type {
  TopBarNotice,
  TopBarNoticeInput,
} from '../types/top-bar-notice-types';
import { TopBarNoticeKind } from '../types/top-bar-notice-enums';


export function selectTopBarNotice({
  onboardingStep,
  healthStatus,
}: TopBarNoticeInput): TopBarNotice | null {
  // 1. Onboarding (Shopify first, then the next incomplete step).
  if (onboardingStep) {
    return {
      kind: TopBarNoticeKind.ONBOARDING,
      tone: BANNER_TONE.NEUTRAL,
      message: `Finish setup: ${onboardingStep.label}`,
      actionLabel: 'Open Setup Guide',
      // Route into the Setup Guide (/get-started decides focused vs app-shell).
      href: '/get-started',
      signal: `onboarding:${onboardingStep.id}`,
    };
  }

  // 2. Health — only real problems (unhealthy/unknown). `not_connected` means a
  // data source was never connected, which onboarding already surfaces above.
  if (healthStatus === 'unhealthy') {
    return {
      kind: TopBarNoticeKind.HEALTH_UNHEALTHY,
      tone: BANNER_TONE.DANGER,
      message: 'One or more data sources lost connection and is no longer syncing.',
      actionLabel: 'Data Sources',
      href: '/integrations',
      signal: 'health:unhealthy',
    };
  }

  if (healthStatus === 'unknown') {
    return {
      kind: TopBarNoticeKind.HEALTH_UNKNOWN,
      tone: BANNER_TONE.WARNING,
      message: 'One or more data sources have an unknown status.',
      actionLabel: 'Data Sources',
      href: '/integrations',
      signal: 'health:unknown',
    };
  }

  return null;
}

const CRITICAL_RECOVERY_REASONS = new Set(['pending', 'past-due', 'unpaid']);

export function isSubscriptionOnTrial(
  subscription: { status?: SUBSCRIPTION_STATUS | string; trialEndsOn?: string | null } | null | undefined
): boolean {
  if (!subscription) {
    return false;
  }
  const status = subscription.status as SUBSCRIPTION_STATUS | undefined;
  return getEffectiveTrialState(subscription.trialEndsOn, status).isOnTrial;
}

export function shouldDeferBillingToOnboarding(input: {
  isTrialing: boolean;
  onboardingIncomplete: boolean;
  recoveryReason: string | null;
}): boolean {
  const { isTrialing, onboardingIncomplete, recoveryReason } = input;
  if (!isTrialing || !onboardingIncomplete || !recoveryReason) {
    return false;
  }
  return !CRITICAL_RECOVERY_REASONS.has(recoveryReason);
}
